import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { jsPDF } from "jspdf";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Collapse from "@mui/material/Collapse";
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import GetAppIcon from '@mui/icons-material/GetApp';
import OutlinedInput from '@mui/material/OutlinedInput';
import MDSnackbar from "components/MDSnackbar";

/**
 * ShareProfileDialog
 * Props:
 * - open: bool
 * - onClose: fn
 * - profile: object
 * - onShare: fn({ type, fields }) optional callback when user confirms
 */
export default function ShareProfileDialog({ open, onClose, profile, onShare, educationEntries = [], workEntries = [], idCardEntries = [], bankEntries = [], addressEntries = [] }) {
  // make 'text' the default share type and keep 'link' as an option (moved to last)
  const [type, setType] = useState("text");
  const [isSharing, setIsSharing] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [expiryTime, setExpiryTime] = useState(1 * 24 * 60 * 60 * 1000);
  const [generatedLink, setGeneratedLink] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [allowDownload, setAllowDownload] = useState(false);

  // reset share state when dialog closes so a fresh open starts clean
  useEffect(() => {
    if (!open) {
      setType("text");
      if (pdfUrl && typeof URL !== "undefined") {
        URL.revokeObjectURL(pdfUrl);
      }
      setPdfUrl(null);
      setPdfGenerating(false);
      setIsSharing(false);
      setExpiryTime(1 * 24 * 60 * 60 * 1000);
      setAllowDownload(false);
      setGeneratedLink(null);
    }
  }, [open, pdfUrl]);

  // clear generated PDF when switching away from PDF type
  useEffect(() => {
    if (type !== "pdf" && pdfUrl) {
      if (typeof URL !== "undefined") {
        URL.revokeObjectURL(pdfUrl);
      }
      setPdfUrl(null);
    }
    if (type !== "link") {
      setGeneratedLink(null);
    }
  }, [type, pdfUrl]);

  useEffect(() => () => {
    if (pdfUrl && typeof URL !== "undefined") {
      URL.revokeObjectURL(pdfUrl);
    }
  }, [pdfUrl]);

  // Selections per section. Keys: basic, personal, address, education, work, idcards, bank
  const [selections, setSelections] = useState({});
  // control collapsed state per section (true = collapsed/hidden details)
  const [collapsed, setCollapsed] = useState({
    basic: true,
    personal: true,
    address: true,
    education: true,
    work: true,
    idcards: true,
    bank: true,
  });

  // Helpers to prettify keys
  const prettyLabel = (key) =>
    String(key)
      .replace(/_c$/i, "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (s) => s.toUpperCase());

  // Decode HTML entities (for cases like Bachelor&#039;s)
  const decodeHtml = (html) => {
    if (!html) return html;
    try {
      const txt = document.createElement("textarea");
      txt.innerHTML = html;
      return txt.value;
    } catch (e) {
      return html;
    }
  };

  // Helper: produce a safe filename for downloads using profile name
  const safeFilename = (base) => {
    if (!base) return 'profile_profile.pdf';
    const s = String(base).trim();
    const cleaned = s.replace(/[^a-z0-9\-_. ]/gi, '_').replace(/\s+/g, '_');
    return `${cleaned}_profile.pdf`;
  };
  useEffect(() => {
    if (!open) return;

    // Build merged basic+personal fields in requested order
    const basic = {};
    const orderedFields = [
      { key: "name", label: "Name", get: () => {
        const first = profile?.first_name?.value ?? profile?.first_name ?? "";
        const last = profile?.last_name?.value ?? profile?.last_name ?? "";
        const uname = profile?.user_name?.value ?? profile?.user_name ?? "";
        const name = (first || last) ? `${first} ${last}`.trim() : (uname || "");
        return name || null;
      } },
      { key: "email", label: "Email", get: () => profile?.email_c?.value ?? profile?.email_c ?? profile?.email ?? null },
      { key: "mobile", label: "Mobile", get: () => profile?.phone_mobile?.value ?? profile?.phone_mobile ?? profile?.mobile ?? null },
      { key: "dob_c", label: "Date of Birth", get: () => profile?.dob_c?.value ?? profile?.dob_c ?? null },
      { key: "age_c", label: "Age", get: () => profile?.age_c?.value ?? profile?.age_c ?? null },
      { key: "gender_c", label: "Gender", get: () => profile?.gender_c?.value ?? profile?.gender_c ?? null },
      { key: "bloodgroup_c", label: "Blood Group", get: () => profile?.bloodgroup_c?.value ?? profile?.bloodgroup_c ?? null },
      { key: "birthplace_c", label: "Birth Place", get: () => profile?.birthplace_c?.value ?? profile?.birthplace_c ?? null },
      { key: "mothertongue_c", label: "Mother Tongue", get: () => profile?.mothertongue_c?.value ?? profile?.mothertongue_c ?? null },
      { key: "languagesknown_c", label: "Languages Known", get: () => profile?.languagesknown_c?.value ?? profile?.languagesknown_c ?? null },
      { key: "aadhaar_c", label: "Aadhaar", get: () => profile?.aadhaar_c?.value ?? profile?.aadhaar_c ?? null },
      { key: "caste_c", label: "Caste", get: () => profile?.caste_c?.value ?? profile?.caste_c ?? null },
      { key: "religion_c", label: "Religion", get: () => profile?.religion_c?.value ?? profile?.religion_c ?? null },
      { key: "disability_c", label: "Disability", get: () => profile?.disability_c?.value ?? profile?.disability_c ?? null },
      { key: "alivestatus_c", label: "Alive Status", get: () => profile?.alivestatus_c?.value ?? profile?.alivestatus_c ?? null },
      { key: "dateofdeath_c", label: "Date of Death", get: () => profile?.dateofdeath_c?.value ?? profile?.dateofdeath_c ?? null },
      { key: "deathplace_c", label: "Death Place", get: () => profile?.deathplace_c?.value ?? profile?.deathplace_c ?? null },
      { key: "deathreason_c", label: "Death Reason", get: () => profile?.deathreason_c?.value ?? profile?.deathreason_c ?? null },
      { key: "deathaddress_c", label: "Death Address", get: () => profile?.deathaddress_c?.value ?? profile?.deathaddress_c ?? null },
      { key: "deatharea_c", label: "Death Area", get: () => profile?.deatharea_c?.value ?? profile?.deatharea_c ?? null },
      { key: "deathpincode_c", label: "Death Pincode", get: () => profile?.deathpincode_c?.value ?? profile?.deathpincode_c ?? null },
      { key: "deathcity_c", label: "Death City", get: () => profile?.deathcity_c?.value ?? profile?.deathcity_c ?? null },
      { key: "deathstate_c", label: "Death State", get: () => profile?.deathstate_c?.value ?? profile?.deathstate_c ?? null },
      { key: "deathcountry_c", label: "Death Country", get: () => profile?.deathcountry_c?.value ?? profile?.deathcountry_c ?? null },
      { key: "maritalstatus_c", label: "Marital Status", get: () => profile?.maritalstatus_c?.value ?? profile?.maritalstatus_c ?? null },
      { key: "marriagedate_c", label: "Marriage Date", get: () => profile?.marriagedate_c?.value ?? profile?.marriagedate_c ?? null },
      { key: "marriageplace_c", label: "Marriage Place", get: () => profile?.marriageplace_c?.value ?? profile?.marriageplace_c ?? null },
    ];

    orderedFields.forEach((f) => {
      try {
        const val = typeof f.get === 'function' ? f.get() : null;
        if (val !== undefined && val !== null && String(val).trim() !== "") {
          basic[f.key] = { checked: true, label: f.label, value: String(val) };
        }
      } catch (e) {
        // ignore
      }
    });

    // address entries: show each entry by id with a label
    const addr = {};
    (addressEntries || []).forEach((entry) => {
      const atype = (entry.addresstype_c || entry.addresstype || "").toString().toLowerCase();
      let label = entry.address || entry.street || entry.village || entry.name || `Address ${entry.id}`;
      if (atype.includes("current")) label = "Current Address";
      else if (atype.includes("perman") || atype.includes("perm")) label = "Permanent Address";
      addr[entry.id] = { checked: true, label };
    });

    const edu = {};
    (educationEntries || []).forEach((entry) => {
      const raw = entry.qualification || entry.name || entry.course || `Education ${entry.id}`;
      const label = decodeHtml(String(raw));
      edu[entry.id] = { checked: true, label };
    });

    const wk = {};
    (workEntries || []).forEach((entry) => {
      const raw = entry.companyname || entry.name || entry.designation || `Work ${entry.id}`;
      const label = decodeHtml(String(raw));
      wk[entry.id] = { checked: true, label };
    });

    const ids = {};
    (idCardEntries || []).forEach((entry) => {
      // Prefer id card type (idtype) and include name if available: "Passport - John Doe"
      const idType = entry.idtype || entry.idcardtype || entry.idtype_c || "";
      const name = entry.name || entry.idcardname || entry.idcardname_c || "";
      const raw = idType ? `${idType}${name ? ` - ${name}` : ""}` : (name || `ID ${entry.id}`);
      const label = decodeHtml(String(raw));
      ids[entry.id] = { checked: true, label };
    });

    const bk = {};
    (bankEntries || []).forEach((entry) => {
      const raw = entry.bankname || entry.name || entry.accountholdername || `Bank ${entry.id}`;
      const label = decodeHtml(String(raw));
      bk[entry.id] = { checked: true, label };
    });

    setSelections({
      basic,
      personal: {},
      address: addr,
      education: edu,
      work: wk,
      idcards: ids,
      bank: bk,
    });
  }, [open, profile, educationEntries, workEntries, idCardEntries, bankEntries, addressEntries]);

  // Show loading overlay when dialog opens and any of the required data props are still undefined/null
  useEffect(() => {
    if (!open) {
      setLoadingData(false);
      return;
    }
    const inputs = [profile, educationEntries, workEntries, idCardEntries, bankEntries, addressEntries];
    const anyFetching = inputs.some((v) => v === null || typeof v === 'undefined');
    setLoadingData(anyFetching);
  }, [open, profile, educationEntries, workEntries, idCardEntries, bankEntries, addressEntries]);

  const toggleSelection = (section, key) => {
    setSelections((prev) => {
      const sectionMap = { ...(prev[section] || {}) };
      if (sectionMap[key] && typeof sectionMap[key] === "object") {
        sectionMap[key].checked = !sectionMap[key].checked;
      } else {
        sectionMap[key] = !sectionMap[key];
      }
      return { ...prev, [section]: sectionMap };
    });
  };

  const toggleSelectAll = (section) => {
    setSelections((prev) => {
      const sectionMap = { ...(prev[section] || {}) };
      const keys = Object.keys(sectionMap);
      const allSelected = keys.length > 0 && keys.every((k) => (sectionMap[k].checked ?? sectionMap[k]) === true);
      keys.forEach((k) => {
        if (typeof sectionMap[k] === "object") sectionMap[k].checked = !allSelected;
        else sectionMap[k] = !allSelected;
      });
      return { ...prev, [section]: sectionMap };
    });
  };

  const toggleCollapsed = (section) => {
    setCollapsed((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  // Helper: generate plain text and HTML representations for the selected data
  const generateShareText = (selectedStructured) => {
    // selectedStructured is expected to be the same shape we build below in handleShare:
    // { basic: [keys], personal: [keys], address: [{id,label}], education: [{id,label}], ... }
    const sectOrder = [
      { key: 'basic', title: 'Basic Info' },
      { key: 'personal', title: 'Personal Info' },
      { key: 'address', title: 'Address Details' },
      { key: 'education', title: 'Education' },
      { key: 'work', title: 'Work Details' },
      { key: 'idcards', title: 'ID Cards' },
      { key: 'bank', title: 'Bank Details' },
    ];

    const htmlParts = [];
    const textParts = [];

    const extractYear = (v) => {
      if (!v) return null;
      try {
        const dt = new Date(String(v));
        if (!isNaN(dt.getTime())) return String(dt.getFullYear());
      } catch (e) {}
      const m = String(v).match(/(19|20)\d{2}/);
      return m ? m[0] : null;
    };

    // Optional: include profile name/header if present
    const profileName = (profile && (profile.first_name?.value || profile.first_name || profile.user_name || profile.name)) || '';
    if (profileName) {
      htmlParts.push(`<h2 style="margin:0 0 8px 0">${profileName}</h2>`);
      textParts.push(profileName);
      textParts.push('');
    }

    // Build sections
    sectOrder.forEach((s) => {
      const val = selectedStructured[s.key];
      if (!val || (Array.isArray(val) && val.length === 0)) return;

      // Section header
      htmlParts.push(`<h3 style="margin:8px 0 6px 0"><strong>${s.title}</strong></h3>`);
      textParts.push(s.title.toUpperCase());

      if (s.key === 'basic' || s.key === 'personal') {
        // val is array of { label, value } objects or keys; try to support both
        val.forEach((item) => {
          // item may be a key string (legacy), or { label, value }
          const label = item.label || prettyLabel(item) || item;
          const value = item.value || '';
          htmlParts.push(`<p style="margin:4px 0"><strong>${label}:</strong> ${value}</p>`);
          textParts.push(`${label}: ${value}`);
        });
      } else {
        // val is array of { id, label, entry }
        // helper to pick first non-empty field from variants
        const getFirstField = (entryObj, variants) => {
          for (let i = 0; i < variants.length; i += 1) {
            const k = variants[i];
            if (Object.prototype.hasOwnProperty.call(entryObj, k)) {
              const v = entryObj[k];
              if (v !== undefined && v !== null && String(v).trim() !== '') return v;
            }
            // try lowercase/underscore variants
            const keys = Object.keys(entryObj);
            const match = keys.find((kk) => kk.toLowerCase() === k.toLowerCase() || kk.toLowerCase() === k.replace(/_/g, '').toLowerCase());
            if (match) {
              const v = entryObj[match];
              if (v !== undefined && v !== null && String(v).trim() !== '') return v;
            }
          }
          return null;
        };

        val.forEach((entryObj, idx) => {
          const entry = entryObj.entry || {};
          // For education prefer qualification as the main label (and decode HTML entities)
          let label = entryObj.label || entryObj.id || `Item ${idx + 1}`;
          if (s.key === 'education') {
            const qual = getFirstField(entry, ['qualification', 'qualification_c', 'course', 'degree', 'name']);
            if (qual) label = decodeHtml(String(qual));
          }
          htmlParts.push(`<div class="mc-entry" style="margin:6px 0;padding:8px;border-radius:4px;background:#fafafa"><div class="mc-entry-title" style="margin:2px 0;font-weight:700">${label}</div>`);
          textParts.push(`${label}`);

          // common skip patterns
          const skipPatterns = ['date_entered', 'date_modified', 'deleted', 'deleted_c', 'parent', 'parent_id', 'parentid', 'image', 'photo', 'picture', 'currentlyworking', 'currently_studying', 'currentlystudying', 'currently_working', 'currentlyworking_c', 'currentlystudying_c'];

          // Section-specific handling
          if (s.key === 'education') {
            // show Qualification prominently (prefer qualification/course/degree), hide 'name' if qualification exists
            const qualification = getFirstField(entry, ['qualification', 'qualification_c', 'course', 'degree', 'name']);

            // start/end dates and year handling
            const start = getFirstField(entry, ['start_date', 'startdate', 'from_date', 'fromdate', 'date_from', 'from', 'joiningdate']);
            const end = getFirstField(entry, ['end_date', 'enddate', 'to_date', 'todate', 'date_to', 'to', 'leavingdate']);
            const isCurrent = !!getFirstField(entry, ['currentlystudying', 'currently_studying', 'currentlystudying_c', 'currently_studying_c', 'is_current', 'current', 'ongoing']);
            if (start) {
              htmlParts.push(`<div class="mc-field" style="margin:2px 0;display:flex;gap:8px;flex-wrap:wrap"><div class="mc-label" style="min-width:120px;font-weight:700">Start Date:</div><div class="mc-value" style="flex:1;word-break:break-word;white-space:pre-wrap">${String(start)}</div></div>`);
              textParts.push(`Start Date: ${String(start)}`);
            }
            if (isCurrent) {
              htmlParts.push(`<div class="mc-field" style="margin:2px 0;display:flex;gap:8px;flex-wrap:wrap"><div class="mc-label" style="min-width:120px;font-weight:700">End Date:</div><div class="mc-value" style="flex:1">Present</div></div>`);
              textParts.push(`End Date: Present`);
            } else if (end) {
              htmlParts.push(`<div class="mc-field" style="margin:2px 0;display:flex;gap:8px;flex-wrap:wrap"><div class="mc-label" style="min-width:120px;font-weight:700">End Date:</div><div class="mc-value" style="flex:1;word-break:break-word;white-space:pre-wrap">${String(end)}</div></div>`);
              textParts.push(`End Date: ${String(end)}`);
            }

            // show Start Year and End Year stacked below (End Year shows 'Present' when current)
            const sYear = extractYear(start);
            const eYear = isCurrent ? 'Present' : extractYear(end);
            if (sYear) {
              htmlParts.push(`<div class="mc-field" style="margin:2px 0;display:flex;gap:8px;flex-wrap:wrap"><div class="mc-label" style="min-width:120px;font-weight:700">Start Year:</div><div class="mc-value" style="flex:1">${sYear}</div></div>`);
              textParts.push(`Start Year: ${sYear}`);
            }
            if (eYear) {
              htmlParts.push(`<div class="mc-field" style="margin:2px 0;display:flex;gap:8px;flex-wrap:wrap"><div class="mc-label" style="min-width:120px;font-weight:700">End Year:</div><div class="mc-value" style="flex:1">${eYear}</div></div>`);
              textParts.push(`End Year: ${eYear}`);
            }

            // now render remaining fields except qualification/name/dates/current flags
            Object.keys(entry).forEach((fieldKey) => {
              if (fieldKey === 'id') return;
              const fk = String(fieldKey).toLowerCase();
              if (skipPatterns.some((p) => fk === p || fk.includes(p))) return;
              // skip name if qualification was already shown
              if (fk === 'name' && qualification) return;
              // skip date fields we've already shown
              if (['start_date', 'startdate', 'from_date', 'fromdate', 'date_from', 'from', 'joiningdate', 'end_date', 'enddate', 'to_date', 'todate', 'date_to', 'to', 'leavingdate'].some((d) => fk === d || fk.includes(d))) return;
              const raw = entry[fieldKey];
              if (raw === undefined || raw === null || String(raw).trim() === '') return;
              const pretty = prettyLabel(fieldKey);
              htmlParts.push(`<div class="mc-field" style="margin:2px 0;display:flex;gap:8px;flex-wrap:wrap"><div class="mc-label" style="min-width:120px;font-weight:700">${pretty}:</div><div class="mc-value" style="flex:1;word-break:break-word;white-space:pre-wrap">${String(raw)}</div></div>`);
              textParts.push(`${pretty}: ${String(raw)}`);
            });

          } else if (s.key === 'work') {
            // For work: show main label (company), then designation if present, then start/end dates
            const designation = getFirstField(entry, ['designation', 'role', 'position', 'job_title']);
            if (designation) {
              htmlParts.push(`<div class="mc-field" style="margin:2px 0;display:flex;gap:8px;flex-wrap:wrap"><div class="mc-label" style="min-width:120px;font-weight:700">Designation:</div><div class="mc-value" style="flex:1;word-break:break-word;white-space:pre-wrap">${String(designation)}</div></div>`);
              textParts.push(`Designation: ${String(designation)}`);
            }
            const start = getFirstField(entry, ['start_date', 'startdate', 'from_date', 'fromdate', 'date_from', 'from', 'joiningdate']);
            const end = getFirstField(entry, ['end_date', 'enddate', 'to_date', 'todate', 'date_to', 'to', 'leavingdate']);
            const isCurrent = !!getFirstField(entry, ['currentlyworking', 'currently_working', 'currentlyworking_c', 'currentlyworking_c', 'is_current', 'current', 'ongoing']);
            if (start) {
              htmlParts.push(`<p style="margin:2px 0"><strong>Start Date:</strong> ${String(start)}</p>`);
              textParts.push(`Start Date: ${String(start)}`);
            }
            if (isCurrent) {
              htmlParts.push(`<p style="margin:2px 0"><strong>End Date:</strong> Present</p>`);
              textParts.push(`End Date: Present`);
            } else if (end) {
              htmlParts.push(`<p style="margin:2px 0"><strong>End Date:</strong> ${String(end)}</p>`);
              textParts.push(`End Date: ${String(end)}`);
            }

            // show Start Year and End Year stacked below (End Year shows 'Present' when current)
            const sYear = extractYear(start);
            const eYear = isCurrent ? 'Present' : extractYear(end);
            if (sYear) {
              htmlParts.push(`<p style="margin:2px 0"><strong>Start Year:</strong> ${sYear}</p>`);
              textParts.push(`Start Year: ${sYear}`);
            }
            if (eYear) {
              htmlParts.push(`<p style="margin:2px 0"><strong>End Year:</strong> ${eYear}</p>`);
              textParts.push(`End Year: ${eYear}`);
            }

            // render remaining fields except skipped ones and dates/current flags
            Object.keys(entry).forEach((fieldKey) => {
              if (fieldKey === 'id') return;
              const fk = String(fieldKey).toLowerCase();
              if (skipPatterns.some((p) => fk === p || fk.includes(p))) return;
              if (['start_date', 'startdate', 'from_date', 'fromdate', 'date_from', 'from', 'joiningdate', 'end_date', 'enddate', 'to_date', 'todate', 'date_to', 'to', 'leavingdate'].some((d) => fk === d || fk.includes(d))) return;
              const raw = entry[fieldKey];
              if (raw === undefined || raw === null || String(raw).trim() === '') return;
              const pretty = prettyLabel(fieldKey);
              htmlParts.push(`<p style="margin:2px 0"><strong>${pretty}:</strong> ${String(raw)}</p>`);
              textParts.push(`${pretty}: ${String(raw)}`);
            });

          } else {
            // default rendering for other sections
            Object.keys(entry).forEach((fieldKey) => {
              if (fieldKey === 'id') return;
              const fk = String(fieldKey).toLowerCase();
              if (skipPatterns.some((p) => fk === p || fk.includes(p))) return;
              const raw = entry[fieldKey];
              if (raw === undefined || raw === null || String(raw).trim() === '') return;
              const pretty = prettyLabel(fieldKey);
              htmlParts.push(`<p style="margin:2px 0"><strong>${pretty}:</strong> ${String(raw)}</p>`);
              textParts.push(`${pretty}: ${String(raw)}`);
            });
          }

          htmlParts.push('</div>');
          textParts.push('');
        });
      }

      textParts.push('');
      htmlParts.push('<hr style="border:none;border-top:1px solid #eee;margin:8px 0"/>');
    });

    const style = `<style>
      .mc-container{font-family: Arial, Helvetica, sans-serif; color: #222; padding: 18px; line-height: 1.45}
      .mc-entry{margin:6px 0; padding:8px; border-radius:4px; background:#fafafa}
      .mc-entry-title{font-weight:700; margin-bottom:6px}
      .mc-field{display:flex; gap:10px; align-items:flex-start; flex-wrap:wrap; margin:4px 0}
      .mc-label{min-width:130px; font-weight:700; color:#333}
      .mc-value{flex:1; word-break:break-word; white-space:pre-wrap}
      h2{margin:0 0 8px 0; font-size:18px}
      h3{margin:12px 0 6px 0; font-size:14px}
      hr{border:none; border-top:1px solid #eee; margin:12px 0}
    </style>`;

    const html = `<div class="mc-container">${style}${htmlParts.join('')}</div>`;
    const text = textParts.join('\n');
    return { text, html };
  };

// Simple approach - let html2canvas handle the full content and split manually
const generatePdfFromStructured = async (structured) => {
  try {
    // Extract plain text content from structured data
    const { html } = generateShareText(structured);

    // Create a hidden container to parse the HTML
    const container = document.createElement('div');
    container.innerHTML = html;
    document.body.appendChild(container);

    // Get all readable text (ignore visual-only elements)
    const textContent = container.innerText || container.textContent;
    document.body.removeChild(container);

    // Create PDF
    const pdf = new jsPDF('p', 'mm', 'a4');
    const margin = 15;
    const pageWidth = pdf.internal.pageSize.getWidth() - margin * 2;
    const pageHeight = pdf.internal.pageSize.getHeight() - margin * 2;
    const lineHeight = 8;

    // Split text into lines to fit within the PDF width
    const lines = pdf.splitTextToSize(textContent.trim(), pageWidth);

    let y = margin;

    lines.forEach((line) => {
      if (y + lineHeight > pageHeight + margin) {
        pdf.addPage();
        y = margin;
      }
      pdf.text(line, margin, y);
      y += lineHeight;
    });

    // Export PDF as Blob URL (same return type as before)
    const pdfBlob = pdf.output('blob');
    const blobUrl = URL.createObjectURL(pdfBlob);

    return blobUrl;
  } catch (error) {
    console.error('Error generating structured text PDF:', error);
    throw error;
  }
};



  // removed legacy simple fields state; selections is initialized when dialog opens

  const handleCopyLink = async () => {
    if (!generatedLink) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(generatedLink);
      } else {
        // fallback
        const ta = document.createElement("textarea");
        ta.value = generatedLink;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setSnackbarMessage("Link copied successfully!");
      setSnackbarOpen(true);
    } catch (err) {
      setSnackbarMessage("Failed to copy link");
      setSnackbarOpen(true);
    }
  };

const handleShare = async () => {
  setIsSharing(true);

  const selected = {};
  const sectionSource = {
    address: addressEntries || [],
    education: educationEntries || [],
    work: workEntries || [],
    idcards: idCardEntries || [],
    bank: bankEntries || [],
  };

  Object.entries(selections || {}).forEach(([section, map]) => {
    if (!map) return;
    if (section === "basic" || section === "personal") {
      selected[section] = Object.keys(map)
        .filter((k) => map[k] === true || (map[k] && map[k].checked === true))
        .map((k) => {
          const item = map[k] || {};
          const label = item.label || prettyLabel(k) || k;
          const value = item.value || (profile && (profile[k]?.value || profile[k])) || "";
          return { label, value };
        });
    } else {
      selected[section] = Object.entries(map)
        .filter(([, v]) => (v && v.checked) || v === true)
        .map(([k, v]) => {
          const arr = sectionSource[section] || [];
          const found = arr.find(
            (e) =>
              e &&
              (String(e.id) === String(k) ||
                String(e.id) === String(v?.id) ||
                String(e?.id) === String(k))
          );
          return { id: k, label: v?.label || k, entry: found || {} };
        });
    }
  });

  // ----- SHARE LINK -----
  if (type === "link") {
    try {
      const userId = profile?.user_id || profile?.id || "";
      if (!userId) throw new Error("User ID missing");

      const structuredSelections = {
        userId: userId.value || userId,
        expiryTime: expiryTime,
        allowDownload: allowDownload,
        basicInfo: {},
        education: [],
        workDetails: [],
        idCards: [],
        bankDetails: [],
        addressDetails: [],
      };

      if (selections.basic) {
        Object.entries(selections.basic).forEach(([key, value]) => {
          if (value && value.checked) {
            structuredSelections.basicInfo[key] = true;
          }
        });
      }

      const sectionMap = {
        education: "education",
        work: "workDetails",
        idcards: "idCards",
        bank: "bankDetails",
        address: "addressDetails",
      };

      Object.entries(sectionMap).forEach(([frontendKey, backendKey]) => {
        if (selections[frontendKey]) {
          Object.entries(selections[frontendKey]).forEach(([id, value]) => {
            if (value && value.checked) {
              structuredSelections[backendKey].push(id);
            }
          });
        }
      });

      const response = await fetch(
        "https://seltentechnologiesdemo.com/moona-backend/generateShareLink.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(structuredSelections),
        }
      );

      const text = await response.text();
      let result;
      try {
        result = JSON.parse(text);
      } catch {
        throw new Error("Invalid JSON from server");
      }

      if (result.status === "success" && result.shareLink) {
        setGeneratedLink(result.shareLink);
        if (onShare)
          onShare({ type, selections: selected, success: true, url: result.shareLink });
      } else {
        throw new Error(result.message || "Failed to generate link");
      }
    } catch (err) {
      if (onShare)
        onShare({
          type,
          selections: selected,
          success: false,
          message: err.message || "API request failed",
        });
    } finally {
      setIsSharing(false);
    }
    return;
  }

  // ----- COPY TEXT -----
  if (type === "text") {
    try {
      const structured = {};
      ["basic", "personal"].forEach((sec) => {
        const arr = selected[sec] || [];
        structured[sec] = arr
          .map((it) => {
            if (!it) return null;
            if (typeof it === "string") {
              const item = (selections[sec] && selections[sec][it]) || {};
              return {
                label: item.label || prettyLabel(it) || it,
                value:
                  item.value ||
                  (profile && (profile[it]?.value || profile[it])) ||
                  "",
              };
            }
            return {
              label: it.label || prettyLabel(it.key || it.name || ""),
              value: it.value || "",
            };
          })
          .filter(Boolean);
      });
      ["address", "education", "work", "idcards", "bank"].forEach((sec) => {
        structured[sec] = selected[sec] || [];
      });

      const { text, html } = generateShareText(structured);

      if (navigator.clipboard && navigator.clipboard.write && window.ClipboardItem) {
        try {
          const blobPlain = new Blob([text], { type: "text/plain" });
          const blobHtml = new Blob([html], { type: "text/html" });
          const item = new ClipboardItem({
            "text/plain": blobPlain,
            "text/html": blobHtml,
          });
          await navigator.clipboard.write([item]);
        } catch (e) {
          await navigator.clipboard.writeText(text);
        }
      } else if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }

      const preview =
        String(text).length > 300 ? `${String(text).slice(0, 300)}...` : String(text);
      if (onShare)
        onShare({ type, selections: selected, success: true, preview });
    } catch (err) {
      if (onShare)
        onShare({
          type,
          selections: selected,
          success: false,
          message: "Failed to copy text",
        });
    } finally {
      setIsSharing(false);
      onClose();
    }
    return;
  }

  // ----- GENERATE CLEAN TEXT-ONLY PDF -----
  if (type === "pdf") {
    try {
      setPdfGenerating(true);
      if (pdfUrl && typeof URL !== "undefined") {
        URL.revokeObjectURL(pdfUrl);
      }
      setPdfUrl(null);

      const structured = {};
      ["basic", "personal"].forEach((sec) => {
        structured[sec] = selected[sec] || [];
      });
      ["address", "education", "work", "idcards", "bank"].forEach((sec) => {
        structured[sec] = selected[sec] || [];
      });

      // ✅ Use clean text-only version of PDF generator
      const blobUrl = await generatePdfFromStructured(structured);

      setPdfUrl(blobUrl);
      if (onShare)
        onShare({ type, selections: selected, success: true, url: blobUrl });
    } catch (err) {
      if (onShare)
        onShare({
          type,
          selections: selected,
          success: false,
          message: "Failed to generate PDF",
        });
    } finally {
      setPdfGenerating(false);
      setIsSharing(false);
    }
    return;
  }

  // ----- DEFAULT -----
  if (onShare) {
    onShare({ type, selections: selected, success: true });
  }

  setIsSharing(false);
  onClose();
};


  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
                maxWidth: '75%',
              },
              }}
            >
              <DialogTitle>Share profile</DialogTitle>
              <DialogContent dividers sx={{ position: 'relative' }}>
              {loadingData && (
                <Box sx={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(255,255,255,0.85)', zIndex: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1 }}>
                  <CircularProgress />
                  <Typography variant="body2">Loading profile data…</Typography>
                </Box>
              )}
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Share as
                </Typography>
                <FormControl fullWidth size="small">
                  <InputLabel id="share-type-label">Type</InputLabel>
                  <Select
                  labelId="share-type-label"
                  value={type}
                  label="Type"
                  onChange={(e) => setType(e.target.value)}
                  size="small"
                  input={<OutlinedInput label="Type" sx={{  boxSizing: 'border-box', pl: 1, height: 56 }} />}
                  sx={{ 
                    '& .MuiSelect-select': { display: 'flex', height: 56, alignItems: 'center' },
                    minHeight: 56
                  }}
                  >
                  <MenuItem value="text">Text</MenuItem>
                  <MenuItem value="pdf">PDF</MenuItem>
                  <MenuItem value="link">Link</MenuItem>
                  </Select>
                </FormControl>
                {type === "link" && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Expiry Time
                    </Typography>
                    <FormControl fullWidth size="small">
                      <InputLabel required id="expiry-label">Expiry</InputLabel>
                      <Select
                        labelId="expiry-label"
                        value={expiryTime}
                        label="Expiry"
                        onChange={(e) => setExpiryTime(e.target.value)}
                        size="small"
                        input={<OutlinedInput label="Expiry" sx={{ boxSizing: 'border-box', pl: 1, height: 56 }} />}
                        sx={{ 
                          '& .MuiSelect-select': { display: 'flex', height: 56, alignItems: 'center' },
                          minHeight: 56
                        }}
                      >
                        <MenuItem value={1 * 60 * 1000}>1 Minutes</MenuItem>
                        <MenuItem value={5 * 60 * 1000}>5 Minutes</MenuItem>
                        <MenuItem value={10 * 60 * 1000}>10 Minutes</MenuItem>
                        <MenuItem value={30 * 60 * 1000}>30 Minutes</MenuItem>
                        <MenuItem value={1 * 60 * 60 * 1000}>1 Hour</MenuItem>
                        <MenuItem value={5 * 60 * 60 * 1000}>5 Hours</MenuItem>
                        <MenuItem value={10 * 60 * 60 * 1000}>10 Hours</MenuItem>
                        <MenuItem value={1 * 24 * 60 * 60 * 1000}>1 Day</MenuItem>
                        <MenuItem value={3 * 24 * 60 * 60 * 1000}>3 Days</MenuItem>
                        <MenuItem value={7 * 24 * 60 * 60 * 1000}>7 Days</MenuItem>
                        <MenuItem value={14 * 24 * 60 * 60 * 1000}>14 Days</MenuItem>
                        <MenuItem value={30 * 24 * 60 * 60 * 1000}>30 Days</MenuItem>
                        <MenuItem value={60 * 24 * 60 * 60 * 1000}>60 Days</MenuItem>
                      </Select>
                    </FormControl>
                    <Box sx={{ mt: 2 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={allowDownload}
                            onChange={(e) => setAllowDownload(e.target.checked)}
                            size="small"
                          />
                        }
                        label="Allow Download"
                      />
                    </Box>
                  </Box>
                )}
                </Grid>
                <Grid item xs={12} md={8}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Select sections to include
                  </Typography>
                  <div>
                  {/* Basic section */}
                <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle2">Basic Info</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            selections.basic ? Object.values(selections.basic).every((v) => (v && (v.checked === true || v === true))) : false
                          }
                          onChange={() => toggleSelectAll('basic')}
                        />
                      }
                      label="Select All"
                    />
                    <IconButton size="small" onClick={() => toggleCollapsed('basic')} aria-label={collapsed.basic ? 'Expand' : 'Collapse'}>
                      {collapsed.basic ? '+' : '−'}
                    </IconButton>
                  </Box>
                </Box>
                <Collapse in={!collapsed.basic}>
                  <FormGroup sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
                    {selections.basic && Object.entries(selections.basic).map(([k, obj]) => (
                      <FormControlLabel
                        key={k}
                        control={<Checkbox checked={obj?.checked === true} onChange={() => toggleSelection('basic', k)} />}
                        label={`${obj?.label || prettyLabel(k)}${obj?.value ? `: ${obj.value}` : ''}`}
                      />
                    ))}
                  </FormGroup>
                </Collapse>

                {/* Personal section */}
                {selections.personal && Object.keys(selections.personal).length > 0 && (
                  <Box sx={{ mt: 2, mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="subtitle2">Personal Info</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <FormControlLabel
                            control={<Checkbox checked={Object.values(selections.personal || {}).every((v) => (v && (v.checked === true || v === true)))} onChange={() => toggleSelectAll('personal')} />}
                            label="Select All"
                          />
                          <IconButton size="small" onClick={() => toggleCollapsed('personal')}>{collapsed.personal ? '+' : '−'}</IconButton>
                        </Box>
                      </Box>
                      <Collapse in={!collapsed.personal}>
                        <FormGroup sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
                          {Object.keys(selections.personal).map((k) => (
                            <FormControlLabel
                              key={k}
                              control={<Checkbox checked={selections.personal[k] === true} onChange={() => toggleSelection('personal', k)} />}
                              label={`${prettyLabel(k)}${profile?.[k]?.value ? `: ${profile[k].value}` : ''}`}
                            />
                          ))}
                        </FormGroup>
                      </Collapse>
                  </Box>
                )}

                {/* Address */}
                {selections.address && Object.keys(selections.address).length > 0 && (
                  <Box sx={{ mt: 2, mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle2">Address Details</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FormControlLabel
                          control={<Checkbox checked={Object.keys(selections.address).every((k) => selections.address[k].checked === true)} onChange={() => toggleSelectAll('address')} />}
                          label="Select All"
                        />
                        <IconButton size="small" onClick={() => toggleCollapsed('address')}>{collapsed.address ? '+' : '−'}</IconButton>
                      </Box>
                    </Box>
                    <Collapse in={!collapsed.address}>
                      <FormGroup>
                        {Object.entries(selections.address).map(([id, obj]) => (
                          <FormControlLabel key={id} control={<Checkbox checked={obj.checked} onChange={() => toggleSelection('address', id)} />} label={obj.label} />
                        ))}
                      </FormGroup>
                    </Collapse>
                  </Box>
                )}

                {/* Education */}
                {selections.education && Object.keys(selections.education).length > 0 && (
                  <Box sx={{ mt: 2, mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle2">Education</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FormControlLabel
                          control={<Checkbox checked={Object.keys(selections.education).every((k) => selections.education[k].checked === true)} onChange={() => toggleSelectAll('education')} />}
                          label="Select All"
                        />
                        <IconButton size="small" onClick={() => toggleCollapsed('education')}>{collapsed.education ? '+' : '−'}</IconButton>
                      </Box>
                    </Box>
                    <Collapse in={!collapsed.education}>
                      <FormGroup>
                        {Object.entries(selections.education).map(([id, obj]) => (
                          <FormControlLabel key={id} control={<Checkbox checked={obj.checked} onChange={() => toggleSelection('education', id)} />} label={obj.label} />
                        ))}
                      </FormGroup>
                    </Collapse>
                  </Box>
                )}

                {/* Work */}
                {selections.work && Object.keys(selections.work).length > 0 && (
                  <Box sx={{ mt: 2, mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle2">Work Details</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FormControlLabel
                          control={<Checkbox checked={Object.keys(selections.work).every((k) => selections.work[k].checked === true)} onChange={() => toggleSelectAll('work')} />}
                          label="Select All"
                        />
                        <IconButton size="small" onClick={() => toggleCollapsed('work')}>{collapsed.work ? '+' : '−'}</IconButton>
                      </Box>
                    </Box>
                    <Collapse in={!collapsed.work}>
                      <FormGroup>
                        {Object.entries(selections.work).map(([id, obj]) => (
                          <FormControlLabel key={id} control={<Checkbox checked={obj.checked} onChange={() => toggleSelection('work', id)} />} label={obj.label} />
                        ))}
                      </FormGroup>
                    </Collapse>
                  </Box>
                )}

                {/* ID Cards */}
                {selections.idcards && Object.keys(selections.idcards).length > 0 && (
                  <Box sx={{ mt: 2, mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle2">ID Cards</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FormControlLabel
                          control={<Checkbox checked={Object.keys(selections.idcards).every((k) => selections.idcards[k].checked === true)} onChange={() => toggleSelectAll('idcards')} />}
                          label="Select All"
                        />
                        <IconButton size="small" onClick={() => toggleCollapsed('idcards')}>{collapsed.idcards ? '+' : '−'}</IconButton>
                      </Box>
                    </Box>
                    <Collapse in={!collapsed.idcards}>
                      <FormGroup>
                        {Object.entries(selections.idcards).map(([id, obj]) => (
                          <FormControlLabel key={id} control={<Checkbox checked={obj.checked} onChange={() => toggleSelection('idcards', id)} />} label={obj.label} />
                        ))}
                      </FormGroup>
                    </Collapse>
                  </Box>
                )}

                {/* Bank */}
                {selections.bank && Object.keys(selections.bank).length > 0 && (
                  <Box sx={{ mt: 2, mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle2">Bank Details</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FormControlLabel
                          control={<Checkbox checked={Object.keys(selections.bank).every((k) => selections.bank[k].checked === true)} onChange={() => toggleSelectAll('bank')} />}
                          label="Select All"
                        />
                        <IconButton size="small" onClick={() => toggleCollapsed('bank')}>{collapsed.bank ? '+' : '−'}</IconButton>
                      </Box>
                    </Box>
                    <Collapse in={!collapsed.bank}>
                      <FormGroup>
                        {Object.entries(selections.bank).map(([id, obj]) => (
                          <FormControlLabel key={id} control={<Checkbox checked={obj.checked} onChange={() => toggleSelection('bank', id)} />} label={obj.label} />
                        ))}
                      </FormGroup>
                    </Collapse>
                  </Box>
                )}
              </div>
            </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={isSharing} color="inherit">
          Cancel
        </Button>
        {type === "link" && (
          <Button
            onClick={handleShare}
            variant="contained"
            disabled={isSharing || pdfGenerating || loadingData}
            color="primary"
            sx={{ color: "#fff" }}
          >
            Generate Link
          </Button>
        )}
        {type === "link" && generatedLink && (
          <Button
            onClick={handleCopyLink}
            variant="contained"
            color="primary"
            sx={{ color: "#fff", ml: 1 }}
          >
            Copy Link
          </Button>
        )}
        {type !== "link" && (
          <Button
            onClick={handleShare}
            variant="contained"
            disabled={isSharing || pdfGenerating || loadingData}
            color="primary"
            sx={{ color: "#fff" }}
          >
            {type === "text"
              ? "Copy"
              : type === "pdf"
              ? pdfGenerating
                ? "Generating..."
                : "Generate PDF"
              : `Share as ${type.toUpperCase()}`}
          </Button>
        )}
        {pdfUrl && (
          <Button
            component="a"
            href={pdfUrl}
            download={(() => {
              const full = profile && (profile.first_name?.value || profile.first_name || profile.user_name || profile.user_name?.value || profile.name);
              return safeFilename(full);
            })()}
            variant="contained"
            color="primary"
            startIcon={<GetAppIcon />}
            sx={{ ml: 1, color: '#fff' }}
          >
            Download PDF
          </Button>
        )}
      </DialogActions>
      <MDSnackbar
        color="success"
        icon="check"
        title="Success"
        content={snackbarMessage}
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        close={() => setSnackbarOpen(false)}
        autoHideDuration={2000}
      />
    </Dialog>
  );
}

ShareProfileDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  profile: PropTypes.object,
  onShare: PropTypes.func,
  educationEntries: PropTypes.array,
  workEntries: PropTypes.array,
  idCardEntries: PropTypes.array,
  bankEntries: PropTypes.array,
  addressEntries: PropTypes.array,
};

ShareProfileDialog.defaultProps = {
  educationEntries: [],
  workEntries: [],
  idCardEntries: [],
  bankEntries: [],
  addressEntries: [],
};
